import { Observable } from 'rxjs';

export enum MeetingConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  JOINED = 'joined',
  FAILED = 'failed',
}

export enum MeetingRoomState {
  JOINING = 'joining',
  JOINED = 'joined',
  LEFT = 'left',
  ERROR = 'error',
  ACTIVE = 'ACTIVE',
}

export enum MeetingTrackType {
  AUDIO = 'audio',
  VIDEO = 'video',
  SCREEN = 'screen',
}

export enum MeetingParticipantAction {
  JOINED = 'joined',
  LEFT = 'left',
  MUTED = 'muted',
  UNMUTED = 'unmuted',
  KICKED = 'kicked',
}

export enum MeetingTrackAction {
  ADDED = 'added',
  REMOVED = 'removed',
  MUTED = 'muted',
  UNMUTED = 'unmuted',
  ERROR = 'error',
}

export interface BaseMeetingEvent<T = string> {
  type: T;
  payload?: any;
}

export interface MeetingConnectionEvent extends BaseMeetingEvent<string> {
  state: MeetingConnectionState;
}

export interface MeetingRoomEvent extends BaseMeetingEvent<string> {
  state: MeetingRoomState;
  roomName?: string;
}

export interface MeetingTrackEvent extends BaseMeetingEvent<string> {
  trackType: MeetingTrackType;
  action: MeetingTrackAction;
  participantId?: string;
}

export interface MeetingParticipantEvent extends BaseMeetingEvent<string> {
  action: MeetingParticipantAction;
  participantId: string;
}

export type MeetingEvent =
  | MeetingConnectionEvent
  | MeetingRoomEvent
  | MeetingTrackEvent
  | MeetingParticipantEvent
  | BaseMeetingEvent;

export interface MeetingServiceOptions {
  connectionConfig?: Record<string, any>;
  roomConfig?: Record<string, any>;
  tracks?: {
    types?: MeetingTrackType[];
    constraints?: MediaStreamConstraints;
  };
  logLevel?: string | number;
}

export interface MeetingJoinOptions {
  password?: string;
  createLocalTracks?: boolean;
  tracks?: {
    types?: MeetingTrackType[];
    constraints?: MediaStreamConstraints;
  };
  roomConfig?: Record<string, any>;
  devices?: string[];
  token?: string;
  // When true, overrides the user's saved camera-off preference so the video
  // track is created during initLocalTracks rather than added after join.
  // Used by the Always On Video policy to eliminate the post-join delay.
  forceVideoOn?: boolean;
  // Returns true once a newer room switch has taken over this client. joinMeeting is a long,
  // multi-second operation (exclusive-queue wait, then up to 5 conference.join() retries with
  // backoff), so a switch fired mid-join would otherwise still create and join the conference
  // for the room the user already left — and whichever join finished last would own
  // `this.conference`. Checked before the conference is created and between join retries.
  shouldAbort?: () => boolean;
}

export interface MeetingParticipant {
  id: string;
  displayName?: string;
  isLocalParticipant: boolean;
  tracks: MeetingTrack[];
  userPid: string;
  avatarUrl: string;
  avatarThumbnailUrl?: string | null;
}

export interface MeetingTrack {
  id: string;
  type: MeetingTrackType;
  participantId: string;
  isMuted: boolean;
  streamId?: string;
  stream?: MediaStream;
  nativeTrack?: any;
}

export interface MeetingService<T extends BaseMeetingEvent = MeetingEvent> {
  initialize?(options?: MeetingServiceOptions): Promise<void>;
  joinMeeting(roomName: string, options?: MeetingJoinOptions): Promise<void>;
  leaveMeeting(options: any): Promise<void>;

  muteAudio(): Promise<void>;
  unmuteAudio(): Promise<void>;
  muteVideo(): Promise<void>;
  unmuteVideo(): Promise<void>;

  startScreenShare(): Promise<void>;
  stopScreenShare(): Promise<void>;
  toggleScreenShare(): Promise<void>;

  getLocalParticipant(): MeetingParticipant | null;
  getParticipants(): MeetingParticipant[];
  getParticipantById(participantId: string): MeetingParticipant | null;

  muteParticipant(participantId: string): Promise<void>;
  kickParticipant(participantId: string, reason?: string): Promise<void>;

  setDisplayName(name: string): Promise<void>;
  getConnectionState(): MeetingConnectionState;
  getRoomState(): MeetingRoomState;

  attachTrackToElement(trackId: string, container: HTMLElement): void;
  detachTrackFromElement(trackId: string, container: HTMLElement): void;

  destroy(): Promise<void>;

  readonly events$: Observable<T>;
}
